import { FC, useEffect, useState } from "react"
import { company } from "../../../../../types/slices/companyType"
import {
    Box,
    Drawer,
    Grid,
    IconButton,
    Typography,
    Card,
    CardActionArea,
    CardContent,
    CircularProgress,
    Button
} from "@mui/material"
import { useDocumentOfSalesStore } from "../../../../../hooks/useDocumentOfSalesStore"
import CloseIcon from '@mui/icons-material/Close'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import { DocumentOfSale } from "../../../../../types/slices/documentOfSalesType"
import SaveIcon from '@mui/icons-material/Save'

interface DocumentSaleCompanyDrawerProps {
    open: boolean
    onClose: () => void
    companySelected: company
}

export const DocumentSaleCompanyDrawer: FC<DocumentSaleCompanyDrawerProps> = ({
    open,
    onClose,
    companySelected
}) => {
    const { getDocumentOfSales, getDocumentOfSalesByCompany, updateForCompany } = useDocumentOfSalesStore()

    const [status, setStatus] = useState<boolean>(false)
    const [documentsSale, setDocumentSales] = useState<DocumentOfSale[]>([])
    const [documentSaleSelected, setDocumentSaleSelected] = useState<DocumentOfSale[]>([])
    const [originalSelected, setOriginalSelected] = useState<DocumentOfSale[]>([])

    useEffect(() => {
        if (open) {
            getDataAPI()
        }
    }, [open])

    const getDataAPI = async (): Promise<void> => {
        setStatus(true)

        const response = await getDocumentOfSales('')
        if (response) setDocumentSales(response)

        const responseByCompany = await getDocumentOfSalesByCompany('', { idcompany: companySelected.id })
        if (responseByCompany) {
            setDocumentSaleSelected(responseByCompany)
            setOriginalSelected(responseByCompany)
        }

        setStatus(false)
    }

    const isSelected = (id: number): boolean =>
        documentSaleSelected.some(doc => doc.id === id)

    const toggleSelect = (doc: DocumentOfSale): void => {
        setDocumentSaleSelected(prev =>
            isSelected(doc.id)
                ? prev.filter(d => d.id !== doc.id)
                : [...prev, doc]
        )
    }

    const hasChanges = (): boolean => {
        if (originalSelected.length !== documentSaleSelected.length) return true
        const currentIds = documentSaleSelected.map(d => d.id).sort()
        const originalIds = originalSelected.map(d => d.id).sort()
        return JSON.stringify(currentIds) !== JSON.stringify(originalIds)
    }

    const handleSave = async (): Promise<void> => {
        const resp = await updateForCompany({
            idcompany: companySelected.id,
            idsDocumentSale: documentSaleSelected.map(doc => doc.id)
        })
        if (resp === true) {
            setOriginalSelected(documentSaleSelected)
        }
    }

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: { xs: '100%', sm: 500 },
                    borderRadius: '12px 0 0 12px',
                    backgroundColor: '#fdfdfd',
                },
            }}
        >
            <Grid container spacing={2} sx={{ p: 2 }}>
                <Grid item xs={12}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                        px={2}
                        py={1.5}
                        sx={{
                            backgroundColor: '#f9fafa',
                            borderRadius: 2,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                        }}
                    >
                        <Box>
                            <Typography variant="h6" fontWeight={700} color="primary">
                                Documentos de venta
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{ color: 'text.secondary', mt: 0.2 }}
                            >
                                {companySelected?.business_name || 'Compañía'}
                            </Typography>
                        </Box>

                        <IconButton
                            onClick={onClose}
                            sx={{
                                backgroundColor: 'rgba(0,0,0,0.04)',
                                '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' },
                            }}
                        >
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Grid>

                {status ? (
                    <Grid item xs={12} display="flex" justifyContent="center" alignItems="center" sx={{ py: 4, minHeight: '100vh' }}>
                        <CircularProgress />
                    </Grid>
                ) : (
                    documentsSale.map(doc => (
                        <Grid item xs={12} key={doc.id}>
                            <Card
                                sx={{
                                    borderRadius: 2,
                                    boxShadow: isSelected(doc.id) ? '0px 4px 12px rgba(76, 175, 80, 0.3)' : 1,
                                    transition: 'all 0.2s ease-in-out',
                                    '&:hover': {
                                        boxShadow: '0px 6px 16px rgba(0,0,0,0.1)',
                                        transform: 'translateY(-2px)'
                                    }
                                }}
                            >
                                <CardActionArea onClick={() => toggleSelect(doc)}>
                                    <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                        {isSelected(doc.id) ? (
                                            <CheckCircleIcon sx={{ fontSize: 30, color: '#4caf50' }} />
                                        ) : (
                                            <RadioButtonUncheckedIcon sx={{ fontSize: 30, color: '#b0b0b0' }} />
                                        )}
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={500}>
                                                {doc.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {isSelected(doc.id) ? 'Asignado' : 'No asignado'}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </CardActionArea>
                            </Card>
                        </Grid>
                    ))
                )}

                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button
                        variant='contained'
                        color='primary'
                        onClick={handleSave}
                        disabled={!hasChanges()}
                        sx={{
                            backgroundColor: '#516E80',
                            '&:hover': { backgroundColor: '#435d6b' }
                        }}
                        startIcon={<SaveIcon />}
                    >
                        Guardar
                    </Button>
                </Grid>
            </Grid>
        </Drawer>
    )
}